import React, { FormEvent, useContext, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { errorToast, successToast, ToastContext } from '@/components/Toast/Toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldError, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ResponseError, useApiClient } from '@/utils/api/ApiClient'
import { IdentityApiClient } from '@/utils/api/identityApi'
import {
  LowerCasePattern,
  NumberPattern,
  SymbolPattern,
  UpperCasePattern,
} from '@/utils/formValidation'

const validatePassword = (password: string) => {
  if (!password) return 'Required'
  if (password.length < 12) return 'The password should be at least 12 characters.'
  if (password.length > 128) return 'The password maximum length is 128 characters.'
  if (!LowerCasePattern.test(password)) {
    return 'The password needs contain at least one lower character.'
  }
  if (!UpperCasePattern.test(password)) {
    return 'The password needs contain at least one upper character.'
  }
  if (!NumberPattern.test(password)) return 'The password needs contain at least one digit'
  if (!SymbolPattern.test(password)) {
    return 'The password must contain at least 1 symbol (*@!#%&()^~{}+%$_-)'
  }
}

export const ResetPasswordDialog: React.FC<{
  isOpen: boolean
  onClose: () => void
}> = ({ isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [password, setPassword] = useState('')
  const [isPasswordTouched, setPasswordTouched] = useState(false)
  const setToast = useContext(ToastContext)
  const identityApiClient = useApiClient(IdentityApiClient)

  const [searchParams] = useSearchParams()
  const email = searchParams.get('email')
  const token = searchParams.get('token')
  const passwordError = useMemo(() => validatePassword(password), [password])
  const displayedPasswordError = isPasswordTouched ? passwordError : undefined
  const linkError = !email || !token ? 'This password reset link is invalid.' : undefined

  const handleClose = () => {
    setPassword('')
    setPasswordTouched(false)
    onClose()
  }

  const handleResetPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!email || !token || passwordError) return

    try {
      setIsLoading(true)
      await identityApiClient.completeRequestResetPassword({
        token,
        email,
        password,
      })
      setToast(
        successToast({
          title: 'Password has been updated',
          subtitle: 'Now you can login with new password.',
        })
      )
      handleClose()
    } catch (error) {
      if (error instanceof ResponseError) {
        error.preventDefault()
      }
      setToast(
        errorToast("Something went wrong. Please make sure this password wasn't used before.")
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
          <DialogDescription>
            {linkError || 'Enter your new password below to finish resetting your password.'}
          </DialogDescription>
        </DialogHeader>

        {linkError ? (
          <DialogFooter>
            <DialogClose render={<Button variant='outline'>Close</Button>} />
          </DialogFooter>
        ) : (
          <form className='space-y-4' onSubmit={handleResetPassword}>
            <Field data-invalid={!!displayedPasswordError}>
              <FieldLabel htmlFor='reset-password-new-password'>New Password</FieldLabel>
              <Input
                id='reset-password-new-password'
                type='password'
                placeholder='Enter new password'
                value={password}
                aria-invalid={!!displayedPasswordError}
                onBlur={() => setPasswordTouched(true)}
                onChange={(event) => setPassword(event.target.value)}
              />
              <FieldError>{displayedPasswordError}</FieldError>
            </Field>
            <DialogFooter>
              <DialogClose
                render={
                  <Button type='button' variant='outline'>
                    Cancel
                  </Button>
                }
              />
              <Tooltip disabled={!passwordError}>
                <TooltipTrigger>
                  <Button type='submit' disabled={isLoading || !!passwordError}>
                    Save
                    {isLoading && <Spinner />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side='top'>{passwordError}</TooltipContent>
              </Tooltip>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
